import type { TripHandoverView } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { ApiError } from '../../../shared/shared.api';
import { handOverTrip, listTripHandovers } from '../dispatch.api';

export type HandoversState = { status: 'loading' } | { status: 'loaded'; handovers: TripHandoverView[] } | { status: 'error' };

/** The server's own words for a refusal (not eligible, already on another trip); anything else is a generic failure. */
function refusalMessage(err: unknown): string {
  const body = err instanceof ApiError ? err.body : null;
  const message = typeof body === 'object' && body !== null && 'message' in body ? (body as { message: unknown }).message : null;
  return typeof message === 'string' ? message : 'Could not hand the trip over. Try again.';
}

/** The trip's handover history, and handing it to another driver. A fresh idempotency key for each attempt that the server did not take. */
export function useHandoverTrip(tripId: string, onHandedOver: () => void) {
  const [state, setState] = useState<HandoversState>({ status: 'loading' });
  const [readCount, setReadCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    listTripHandovers(tripId)
      .then((handovers) => !cancelled && setState({ status: 'loaded', handovers }))
      .catch(() => !cancelled && setState({ status: 'error' }));
    return () => {
      cancelled = true;
    };
  }, [tripId, readCount]);

  const handOver = useCallback(
    async (values: { newDriverId: string; reason: string }): Promise<boolean> => {
      setIsSubmitting(true);
      setError(null);
      try {
        const { trip, handover } = await handOverTrip(tripId, { idempotencyKey, ...values });
        toast.success(`Trip ${trip.number} handed to ${handover.to.name}`);
        setIdempotencyKey(crypto.randomUUID());
        setReadCount((count) => count + 1);
        onHandedOver();
        return true;
      } catch (err) {
        setError(refusalMessage(err));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [tripId, idempotencyKey, toast, onHandedOver],
  );

  return { state, handOver, isSubmitting, error, clearError: () => setError(null) };
}
